import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import CustomDataGrid from "../../Components/CustomDataGrid";
import EditIcon from "@mui/icons-material/Edit";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { Lock } from "@mui/icons-material";

const defaultBooleans = {
    isAddDialogOpen: false,
    isEditDialogOpen: false,
};

const UsersPage = () => {
    const authHeader = useAuthHeader();
    const [booleans, setBooleans] = useState(defaultBooleans);
    const [selectedUser, setSelectedUser] = useState(null);
    const [availableEmployeeTypes, setAvailableEmployeeTypes] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(0);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
        useState(false);
    const [changePasswordData, setChangePasswordData] = useState({
        password: "",
        confirmPassword: "",
    });

    const [users, setUsers] = useState([]);
    const columns = [
        { field: "id", headerName: "ID", width: 90 },
        {
            field: "name",
            headerName: "Name",
            width: 150,
        },
        {
            field: "username",
            headerName: "User",
            width: 150,
        },
        {
            field: "employeeType",
            headerName: "Type",
            width: 150,
            valueGetter: (value) => {
                return value.name;
            },
        },
        {
            field: "active",
            headerName: "Active",
            width: 150,
        },
        {
            field: "createdAt",
            headerName: "Created At",
            width: 150,
            valueGetter: (value) => {
                return new Date(value).toLocaleString();
            },
        },
    ];
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/users`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                    }
                );

                const data = await response.json();
                if (response.ok) {
                    setUsers(data);
                }
            } catch (err) {
                console.log(err);
            }
        };

        fetchUsers();
    }, [authHeader]);

    const actions = [
        {
            title: "Edit User",
            color: "primary",
            handler: (id) => {
                setSelectedUserId(id);
                setBooleans({ ...booleans, isEditDialogOpen: true });
            },
            parameters: ["id"],
            icon: <EditIcon />,
        },
        {
            title: "Change Password",
            color: "primary",
            handler: (id) => {
                setSelectedUserId(id);
                setIsChangePasswordDialogOpen(true);
            },
            parameters: ["id"],
            icon: <Lock />,
        },
    ];

    const handleSubmit = async () => {
        try {
            let response = null;

            if (booleans.isAddDialogOpen) {
                response = await fetch(`${REACT_APP_BACKEND_URL}/users`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify(selectedUser),
                });
            } else {
                response = await fetch(
                    `${REACT_APP_BACKEND_URL}/users/${selectedUser.id}`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                        body: JSON.stringify(selectedUser),
                    }
                );
            }

            const data = await response.json();
            if (response.ok) {
                setUsers(data);
                setBooleans(defaultBooleans);
                setAvailableEmployeeTypes([]);
                setSelectedUser(null);
                setSelectedUserId(0);
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        const getUserToEdit = async () => {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/users/${selectedUserId}`,
                {
                    method: "GET",

                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                }
            );

            const data = await response.json();
            if (response.ok) {
                setAvailableEmployeeTypes(data.employeeTypes);
                setSelectedUser(data.user);
            }
        };
        if (booleans.isEditDialogOpen || booleans.isAddDialogOpen) {
            getUserToEdit();
        }
    }, [
        booleans.isEditDialogOpen,
        selectedUserId,
        authHeader,
        booleans.isAddDialogOpen,
    ]);

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold" }}
                    color="primary"
                    gutterBottom
                >
                    Employees
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                        setBooleans({ ...booleans, isAddDialogOpen: true })
                    }
                >
                    Add Employee
                </Button>
            </Box>
            <CustomDataGrid
                columns={columns}
                rows={users}
                actions={actions}
                perPage={20}
                hasEdit={false}
            />
            <Dialog
                open={booleans.isAddDialogOpen || booleans.isEditDialogOpen}
                onClose={() => {
                    setSelectedUser(null);
                    setBooleans(defaultBooleans);
                }}
            >
                <DialogTitle>
                    {booleans.isAddDialogOpen ? "Add User" : "Edit User"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedUser && booleans.isEditDialogOpen && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    direction: "ltr",
                                    py: "10px",
                                }}
                            >
                                <TextField
                                    label="Name"
                                    value={selectedUser?.name}
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            name: e.target.value,
                                        })
                                    }
                                    size="small"
                                />
                                <TextField
                                    label="Username"
                                    value={selectedUser?.username}
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            username: e.target.value,
                                        })
                                    }
                                    size="small"
                                />
                                <TextField
                                    label="Password"
                                    value={"********"}
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            password: e.target.value,
                                        })
                                    }
                                    size="small"
                                />

                                <Select
                                    value={selectedUser?.employeeType?.id}
                                    size="small"
                                    label="Employee Type"
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            employeeTypeId: e.target.value,
                                        })
                                    }
                                >
                                    {availableEmployeeTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        )}
                        {booleans.isAddDialogOpen && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    direction: "ltr",
                                    py: "10px",
                                }}
                            >
                                <TextField
                                    label="الاسم"
                                    size="small"
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            name: e.target.value,
                                        })
                                    }
                                    value={selectedUser?.name}
                                />
                                <TextField
                                    label="اسم المستخدم"
                                    size="small"
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            username: e.target.value,
                                        })
                                    }
                                    value={selectedUser?.username}
                                />
                                <TextField
                                    label="كلمة المرور"
                                    size="small"
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            password: e.target.value,
                                        })
                                    }
                                    value={selectedUser?.password}
                                />
                                <TextField
                                    label="تأكيد كلمة المرور"
                                    size="small"
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            confirmPassword: e.target.value,
                                        })
                                    }
                                    value={selectedUser?.confirmPassword}
                                />
                                <Select
                                    value={selectedUser?.employeeType?.id}
                                    size="small"
                                    label="Employee Type"
                                    onChange={(e) =>
                                        setSelectedUser({
                                            ...selectedUser,
                                            employeeTypeId: e.target.value,
                                        })
                                    }
                                >
                                    {availableEmployeeTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedUser?.active}
                                                onChange={(e) =>
                                                    setSelectedUser({
                                                        ...selectedUser,
                                                        active: e.target
                                                            .checked,
                                                    })
                                                }
                                            />
                                        }
                                        label="مفعل"
                                    />
                                </FormGroup>
                            </Box>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setSelectedUserId(0);
                            setSelectedUser(null);
                            setBooleans(defaultBooleans);
                        }}
                        color="error"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isChangePasswordDialogOpen}
                onClose={() => {
                    setIsChangePasswordDialogOpen(false);
                    setSelectedUserId(0);
                    setChangePasswordData({
                        password: "",
                        confirmPassword: "",
                    });
                }}
            >
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            direction: "ltr",
                            py: "10px",
                        }}
                    >
                        <TextField
                            label="Password"
                            value={changePasswordData.password}
                            onChange={(e) =>
                                setChangePasswordData((prev) => ({
                                    ...prev,
                                    password: e.target.value,
                                }))
                            }
                        />
                        <TextField
                            label="Confirm Password"
                            value={changePasswordData.confirmPassword}
                            onChange={(e) =>
                                setChangePasswordData((prev) => ({
                                    ...prev,
                                    confirmPassword: e.target.value,
                                }))
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsChangePasswordDialogOpen(false);
                            setChangePasswordData({
                                password: "",
                                confirmPassword: "",
                            });
                            setSelectedUserId(0);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            try {
                                const response = await fetch(
                                    `${REACT_APP_BACKEND_URL}/users/${selectedUserId}/change-password`,
                                    {
                                        method: "PATCH",
                                        headers: {
                                            "Content-Type": "application/json",
                                            Authorization: authHeader,
                                        },
                                        body: JSON.stringify(
                                            changePasswordData
                                        ),
                                    }
                                );

                                const data = await response.json();
                                if (response.ok) {
                                    setUsers(data);
                                    setIsChangePasswordDialogOpen(false);
                                    setSelectedUserId(0);
                                    setChangePasswordData({
                                        password: "",
                                        confirmPassword: "",
                                    });
                                }
                            } catch (err) {
                                console.log(err);
                            }
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersPage;
